import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, MailCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Label } from "@/components/atoms/Label";
import { AuthenticationService } from "@/modules/identity/services/AuthenticationService";
import { forgotPasswordSchema, type ForgotPasswordFormValues } from "@/modules/identity/schemas/authSchemas";
import { toast } from "@/hooks/use-toast";
import { ROUTES } from "@/constants/routes";
import { getFirebaseErrorMessage } from "@/utils/firebaseErrors";

export function ForgotPasswordPage() {
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormValues>({ resolver: zodResolver(forgotPasswordSchema) });

  async function onSubmit(values: ForgotPasswordFormValues) {
    try {
      await AuthenticationService.sendPasswordReset(values.email);
      setEmailSent(true);
    } catch (error) {
      toast({ variant: "destructive", title: "Não foi possível enviar o e-mail", description: getFirebaseErrorMessage(error) });
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="mx-auto w-full max-w-sm space-y-6 rounded-2xl bg-card p-6 shadow-elevated"
    >
      <Link to={ROUTES.login} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>

      {emailSent ? (
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-success/15 text-success">
            <MailCheck className="h-7 w-7" />
          </div>
          <div className="space-y-1">
            <p className="font-display text-base font-semibold">Verifique seu e-mail</p>
            <p className="text-sm text-muted-foreground">
              Enviamos um link de recuperação para <strong>{getValues("email")}</strong>.
            </p>
          </div>
          <Button asChild variant="outline" className="mt-2 w-full">
            <Link to={ROUTES.login}>Voltar para o login</Link>
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-1">
            <h1 className="font-display text-xl font-bold">Recuperar senha</h1>
            <p className="text-sm text-muted-foreground">Enviaremos um link para redefinir sua senha</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="email" type="email" placeholder="voce@email.com" className="pl-10" error={errors.email?.message} {...register("email")} />
              </div>
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            </div>

            <Button type="submit" className="w-full" isLoading={isSubmitting}>
              Enviar link de recuperação
            </Button>
          </form>
        </>
      )}
    </motion.div>
  );
}
